import type { WidgetPlacement, WidgetRegistry } from './types';
import { findEmptyPosition } from './findEmptyPosition';

export interface GridDashboardState {
  widgets: WidgetPlacement[];
}

export type GridDashboardAction =
  | {
      type: 'ADD_WIDGET';
      payload: { widgetType: string; config?: Record<string, unknown> };
    }
  | { type: 'REMOVE_WIDGET'; payload: { instanceId: string } }
  | { type: 'UPDATE_LAYOUT'; payload: { widgets: WidgetPlacement[] } };

/**
 * Pure widget-grid reducer. Tab/dashboard-level concerns (which dashboard
 * is active, persistence, naming) are owned by the host context — this
 * reducer only knows about the widget set on a single canvas.
 */
export function gridDashboardReducer(
  state: GridDashboardState,
  action: GridDashboardAction,
  registry: WidgetRegistry,
): GridDashboardState {
  switch (action.type) {
    case 'ADD_WIDGET': {
      const contract = registry.get(action.payload.widgetType);
      if (!contract) {
        console.warn(
          `[gridDashboardReducer] Cannot add widget "${action.payload.widgetType}" — not in registry`,
        );
        return state;
      }
      const w = contract.defaultSize.width;
      const h = contract.defaultSize.height;
      const pos = findEmptyPosition(state.widgets, w, h);
      const placement: WidgetPlacement = {
        instanceId: crypto.randomUUID(),
        widgetType: action.payload.widgetType,
        x: pos.x,
        y: pos.y,
        w,
        h,
        config: action.payload.config,
      };
      return { ...state, widgets: [...state.widgets, placement] };
    }

    case 'REMOVE_WIDGET': {
      return {
        ...state,
        widgets: state.widgets.filter(
          (wp) => wp.instanceId !== action.payload.instanceId,
        ),
      };
    }

    case 'UPDATE_LAYOUT': {
      return { ...state, widgets: action.payload.widgets };
    }

    default:
      return state;
  }
}
